import { useState, useEffect } from 'react';
import { useAuth } from '../lib/auth';
import { supabase } from '../lib/supabaseClient';
import { pinToPassword, LOGIN_ACCOUNTS } from '../lib/pinAuth';
import { verifyPin as gasVerifyPin } from '../lib/apiLegacy';
import { getActiveSite, storeActiveSite, homeSite, roleForSite } from '../lib/sites';

// PIN-only login: type a 4-digit PIN and it auto-submits (no button). Each
// account's PIN is unique, so we try the PIN against every account in turn and
// the match determines both which profile to open AND which location (3CHA
// Kingston / 3CAL Albany) the session starts in.
const LAYOUTS = ['layout-soft', 'layout-brutal', 'layout-glass', 'layout-synth', 'layout-carbon', 'layout-luxe', 'layout-ocean', 'layout-signal'];

export default function DevLogin() {
  const { login } = useAuth();
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  // Match the device's last-active theme (light/dark + layout skin) so the
  // login page looks like the app the user left — no role yet, so the layout
  // comes from the device-wide 3c_last_layout (falls back to the role keys for
  // devices set before that mirror existed).
  useEffect(() => {
    try {
      const light = localStorage.getItem('3c_theme') === 'light';
      document.documentElement.classList.toggle('theme-light', light);
      document.body.classList.toggle('theme-light', light);
      LAYOUTS.forEach((c) => document.body.classList.remove(c));
      const saved = localStorage.getItem('3c_last_layout')
        || localStorage.getItem('3c_owner_layout')
        || localStorage.getItem('3c_principal_layout') || '';
      if (LAYOUTS.includes(saved)) document.body.classList.add(saved);
    } catch (_) {}
  }, []);

  async function attemptLogin(value) {
    setBusy(true);
    setErr('');
    try {
      let matchedUser = null;
      for (const acct of LOGIN_ACCOUNTS) {
        const { data, error } = await supabase.auth.signInWithPassword({ email: acct.email, password: pinToPassword(value) });
        if (!error && data && data.user) { matchedUser = data.user; break; }
      }
      if (!matchedUser) { setErr('Incorrect PIN'); setPin(''); setBusy(false); return; }

      // Start the session at this account's home location, and resolve the role
      // FOR that location (Ric signs in as `hd` because Albany is his home; his
      // Kingston tile hands him `detailer`).
      const home = homeSite(matchedUser);
      const matchedRole = roleForSite(matchedUser, home);
      if (home !== getActiveSite()) {
        // The Supabase client baked `x-3c-site` in at construction, so it is
        // still pointing at the previous location. Persist the correct one and
        // reload — the session survives (it's in localStorage) and the app comes
        // back up already scoped to the right shop. Reloading here is much safer
        // than letting the first screen render against the wrong location.
        storeActiveSite(home);
        window.location.replace('/');
        return;
      }
      // Mint the legacy GAS session token in the BACKGROUND — it's a slow GAS
      // round-trip and login shouldn't block on it. The Supabase session (set by
      // signInWithPassword above) is what gates the app; the GAS token only
      // powers still-legacy endpoints, which aren't hit until after the user
      // picks a program. Stored as 3c_token when it lands.
      gasVerifyPin(matchedRole, value)
        .then((g) => { if (g && g.success && g.token) { try { localStorage.setItem('3c_token', g.token); } catch (_) {} } })
        .catch(() => { /* GAS unreachable — Supabase-backed features still work */ });
      login(matchedRole, undefined, home); // routes immediately via '/' -> HubPicker or the role's shell
    } catch (e2) {
      setErr(e2.message || 'Login error');
      setPin('');
      setBusy(false);
    }
  }

  function onChange(e) {
    if (busy) return;
    const v = e.target.value.replace(/\D/g, '').slice(0, 4);
    setPin(v);
    if (v.length === 4) attemptLogin(v); // auto-submit on the 4th digit
  }

  const wrap = { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg, #0b0b0c)', fontFamily: 'Inter, sans-serif' };
  const card = { width: 300, padding: 28, borderRadius: 14, background: 'var(--surface, #17181b)', border: '1px solid var(--border, #2a2c31)', color: 'var(--text, #e9e9ec)', textAlign: 'center' };
  const field = { width: '100%', padding: '12px 14px', marginTop: 18, borderRadius: 10, border: '1px solid var(--border, #2a2c31)', background: 'var(--bg, #0b0b0c)', color: 'inherit', fontSize: 26, letterSpacing: 10, textAlign: 'center', boxSizing: 'border-box', opacity: busy ? 0.6 : 1 };

  return (
    <div style={wrap}>
      <div style={card}>
        <div style={{ fontSize: 20, fontWeight: 800, letterSpacing: 0.5 }}>3C Detailing</div>
        <div style={{ fontSize: 12, color: 'var(--muted, #9a9ca1)', marginTop: 4 }}>Enter your PIN</div>
        <input
          type="password" inputMode="numeric" autoFocus maxLength={4} value={pin}
          onChange={onChange} disabled={busy} style={field} aria-label="PIN"
        />
        <div style={{ fontSize: 13, marginTop: 12, minHeight: 18, color: err ? '#ef4444' : 'var(--muted, #9a9ca1)' }}>
          {busy ? 'Checking…' : (err || '')}
        </div>
      </div>
    </div>
  );
}
